import React from 'react';

interface LogoProps {
  className?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  color?: 'primary' | 'secondary' | 'accent' | 'white' | 'gray';
  style?: React.CSSProperties;
}

const sizeClasses = {
  sm: 'w-6 h-6',
  md: 'w-8 h-8',
  lg: 'w-12 h-12',
  xl: 'w-16 h-16'
};

const colorClasses = {
  primary: 'text-text',
  secondary: 'text-textSecondary',
  accent: 'text-accent',
  white: 'text-text',
  gray: 'text-textSubtle'
};

export const Logo: React.FC<LogoProps> = ({ 
  className = '', 
  size = 'md', 
  color = 'primary',
  style = {}
}) => {
  return (
    <div 
      className={`${sizeClasses[size]} ${colorClasses[color]} ${className}`}
      style={{
        filter: 'drop-shadow(0 0 0.28px #A7C7FF) drop-shadow(0 0 1.3px #A7C7FF) drop-shadow(0 0 1.6px #A7C7FF) drop-shadow(0 0 2.4px #A7C7FF)',
        ...style
      }}
    >
      <svg 
        xmlns="http://www.w3.org/2000/svg" 
        xmlnsXlink="http://www.w3.org/1999/xlink" 
        viewBox="0 0 375 375" 
        className="w-full h-full fill-current stroke-current"
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <clipPath id="e1764e809a">
            <path d="M 23 13 L 351.96875 13 L 351.96875 372 L 23 372 Z M 23 13 "/>
          </clipPath>
          <clipPath id="f93bca4509">
            <path d="M 32.730469 27 L 343.230469 27 L 343.230469 367 L 32.730469 367 Z M 32.730469 27 "/>
          </clipPath>
          <clipPath id="aab74988b1">
            <path d="M 42.820312 39 L 332.320312 39 L 332.320312 355 L 42.820312 355 Z M 42.820312 39 "/>
          </clipPath>
          <clipPath id="e3343dedb5">
            <path d="M 73.117188 249 L 116.226562 249 L 116.226562 323.035156 L 73.117188 323.035156 Z M 73.117188 249 "/>
          </clipPath>
          <clipPath id="01d9534a15">
            <path d="M 90.535156 249.058594 L 98.976562 249.058594 C 103.550781 249.058594 107.941406 250.878906 111.175781 254.113281 C 114.410156 257.347656 116.226562 261.734375 116.226562 266.308594 L 116.226562 305.773438 C 116.226562 310.347656 114.410156 314.734375 111.175781 317.96875 C 107.941406 321.207031 103.550781 323.023438 98.976562 323.023438 L 90.535156 323.023438 C 85.960938 323.023438 81.570312 321.207031 78.335938 317.96875 C 75.101562 314.734375 73.285156 310.347656 73.285156 305.773438 L 73.285156 266.308594 C 73.285156 261.734375 75.101562 257.347656 78.335938 254.113281 C 81.570312 250.878906 85.960938 249.058594 90.535156 249.058594 Z M 90.535156 249.058594 "/>
          </clipPath>
          <clipPath id="932f44b25a">
            <path d="M 134.976562 206.34375 L 178.3125 206.34375 L 178.3125 357.671875 L 134.976562 357.671875 Z M 134.976562 206.34375 "/>
          </clipPath>
          <clipPath id="4ef64435ce">
            <path d="M 152.226562 206.34375 L 161.0625 206.34375 C 165.636719 206.34375 170.023438 208.160156 173.257812 211.394531 C 176.492188 214.628906 178.3125 219.019531 178.3125 223.59375 L 178.3125 340.332031 C 178.3125 344.90625 176.492188 349.296875 173.257812 352.53125 C 170.023438 355.765625 165.636719 357.582031 161.0625 357.582031 L 152.226562 357.582031 C 147.652344 357.582031 143.265625 355.765625 140.03125 352.53125 C 136.792969 349.296875 134.976562 344.90625 134.976562 340.332031 L 134.976562 223.59375 C 134.976562 219.019531 136.792969 214.628906 140.03125 211.394531 C 143.265625 208.160156 147.652344 206.34375 152.226562 206.34375 Z M 152.226562 206.34375 "/>
          </clipPath>
          <clipPath id="2c5b21b620">
            <path d="M 259.035156 166.007812 L 301.496094 166.007812 L 301.496094 325.769531 L 259.035156 325.769531 Z M 259.035156 166.007812 "/>
          </clipPath>
          <clipPath id="17b84383c1">
            <path d="M 276.453125 166.171875 L 284.246094 166.171875 C 288.820312 166.171875 293.210938 167.992188 296.445312 171.226562 C 299.679688 174.460938 301.496094 178.847656 301.496094 183.421875 L 301.496094 308.472656 C 301.496094 313.046875 299.679688 317.433594 296.445312 320.671875 C 293.210938 323.90625 288.820312 325.722656 284.246094 325.722656 L 276.453125 325.722656 C 271.875 325.722656 267.488281 323.90625 264.253906 320.671875 C 261.019531 317.433594 259.203125 313.046875 259.203125 308.472656 L 259.203125 183.421875 C 259.203125 178.847656 261.019531 174.460938 264.253906 171.226562 C 267.488281 167.992188 271.875 166.171875 276.453125 166.171875 Z M 276.453125 166.171875 "/>
          </clipPath>
          <clipPath id="a96bd18736">
            <path d="M 197.0625 230.40625 L 240.792969 230.40625 L 240.792969 357.675781 L 197.0625 357.675781 Z M 197.0625 230.40625 "/>
          </clipPath>
          <clipPath id="53fbf0e616">
            <path d="M 214.3125 230.40625 L 223.542969 230.40625 C 228.121094 230.40625 232.507812 232.222656 235.742188 235.457031 C 238.976562 238.691406 240.792969 243.078125 240.792969 247.65625 L 240.792969 340.332031 C 240.792969 344.90625 238.976562 349.296875 235.742188 352.53125 C 232.507812 355.765625 228.121094 357.582031 223.542969 357.582031 L 214.3125 357.582031 C 209.734375 357.582031 205.347656 355.765625 202.113281 352.53125 C 198.878906 349.296875 197.0625 344.90625 197.0625 340.332031 L 197.0625 247.65625 C 197.0625 243.078125 198.878906 238.691406 202.113281 235.457031 C 205.347656 232.222656 209.734375 230.40625 214.3125 230.40625 Z M 214.3125 230.40625 "/>
          </clipPath>
          <clipPath id="30f4c2ea78">
            <path d="M 233 118 L 288 118 L 288 167 L 233 167 Z M 233 118 "/>
          </clipPath>
          <clipPath id="093b866760">
            <path d="M 273.09375 95.773438 L 301.535156 141.394531 L 261.671875 166.242188 L 233.230469 120.621094 Z M 273.09375 95.773438 "/>
          </clipPath>
          <clipPath id="d1419c1cef">
            <path d="M 287.292969 118.550781 L 261.632812 166.175781 L 233.230469 120.621094 Z M 287.292969 118.550781 "/>
          </clipPath>
        </defs>
        <g>
          <g clipRule="nonzero" clipPath="url(#e1764e809a)">
            <path d="M 336.359375 244.808594 C 336.359375 264.039062 322.957031 287.480469 306.582031 297.035156 L 217.25 349.40625 C 200.875 358.960938 174.078125 358.960938 157.695312 349.40625 L 68.371094 297.203125 C 51.988281 287.648438 38.59375 264.039062 38.59375 244.976562 L 38.59375 140.527344 C 38.59375 121.273438 51.988281 97.832031 68.371094 88.300781 L 157.695312 36.074219 C 174.078125 26.542969 200.875 26.542969 217.25 36.074219 L 306.582031 88.132812 C 322.957031 97.664062 336.359375 121.273438 336.359375 140.359375 Z M 351.761719 244.808594 L 351.761719 140.359375 C 351.761719 115.835938 335.324219 87.054688 314.339844 74.816406 L 225.003906 22.757812 C 203.964844 10.5 170.992188 10.5 149.945312 22.757812 L 60.59375 75.007812 C 39.625 87.222656 23.191406 116.003906 23.191406 140.527344 L 23.191406 244.976562 C 23.191406 269.5 39.625 298.28125 60.613281 310.496094 L 149.925781 362.71875 C 170.992188 374.980469 203.960938 374.980469 225.003906 362.71875 L 314.375 310.304688 C 335.324219 298.113281 351.761719 269.332031 351.761719 244.808594 Z M 351.761719 244.808594 "/>
          </g>
          <g clipRule="nonzero" clipPath="url(#f93bca4509)">
            <path d="M 328.5 246.164062 C 328.5 264.308594 315.855469 286.429688 300.402344 295.445312 L 216.105469 344.863281 C 200.652344 353.878906 175.363281 353.878906 159.910156 344.863281 L 75.613281 295.605469 C 60.160156 286.589844 47.515625 264.308594 47.515625 246.320312 L 47.515625 147.757812 C 47.515625 129.589844 60.160156 107.46875 75.613281 98.472656 L 159.910156 49.191406 C 175.363281 40.199219 200.652344 40.199219 216.105469 49.191406 L 300.402344 98.316406 C 315.855469 107.308594 328.5 129.589844 328.5 147.597656 Z M 343.035156 246.164062 L 343.035156 147.597656 C 343.035156 124.460938 327.527344 97.300781 307.722656 85.753906 L 223.421875 36.628906 C 203.566406 25.058594 172.453125 25.058594 152.589844 36.628906 L 68.277344 85.933594 C 48.492188 97.457031 32.984375 124.617188 32.984375 147.757812 L 32.984375 246.320312 C 32.984375 269.460938 48.492188 296.621094 68.296875 308.144531 L 152.574219 357.425781 C 172.453125 368.996094 203.5625 368.996094 223.421875 357.425781 L 307.757812 307.964844 C 327.523438 296.464844 343.035156 269.300781 343.035156 246.164062 Z M 343.035156 246.164062 "/>
          </g>
          <g clipRule="nonzero" clipPath="url(#aab74988b1)">
            <path d="M 318.585938 242.710938 C 318.585938 259.636719 306.792969 280.269531 292.378906 288.675781 L 213.757812 334.769531 C 199.34375 343.179688 175.757812 343.179688 161.34375 334.769531 L 82.722656 288.824219 C 68.308594 280.414062 56.515625 259.636719 56.515625 242.859375 L 56.515625 150.929688 C 56.515625 133.984375 68.308594 113.351562 82.722656 104.960938 L 161.34375 58.996094 C 175.757812 50.609375 199.34375 50.609375 213.757812 58.996094 L 292.378906 104.816406 C 306.792969 113.203125 318.585938 133.984375 318.585938 150.78125 Z M 332.144531 242.710938 L 332.144531 150.78125 C 332.144531 129.199219 317.675781 103.867188 299.207031 93.097656 L 220.582031 47.277344 C 202.0625 36.488281 173.042969 36.488281 154.519531 47.277344 L 75.878906 93.265625 C 57.425781 104.015625 42.960938 129.347656 42.960938 150.929688 L 42.960938 242.859375 C 42.960938 264.441406 57.425781 289.773438 75.894531 300.519531 L 154.5 346.488281 C 173.042969 357.277344 202.058594 357.277344 220.582031 346.488281 L 299.238281 300.351562 C 317.675781 289.625 332.144531 264.292969 332.144531 242.710938 Z M 332.144531 242.710938 "/>
          </g>
          <g clipRule="nonzero" clipPath="url(#e3343dedb5)">
            <g clipRule="nonzero" clipPath="url(#01d9534a15)">
              <path d="M 73.285156 249.058594 L 116.226562 249.058594 L 116.226562 323.035156 L 73.285156 323.035156 Z M 73.285156 249.058594 "/>
            </g>
          </g>
          <g clipRule="nonzero" clipPath="url(#932f44b25a)">
            <g clipRule="nonzero" clipPath="url(#4ef64435ce)">
              <path d="M 134.976562 206.34375 L 178.3125 206.34375 L 178.3125 357.503906 L 134.976562 357.503906 Z M 134.976562 206.34375 "/>
            </g>
          </g>
          <g clipRule="nonzero" clipPath="url(#2c5b21b620)">
            <g clipRule="nonzero" clipPath="url(#17b84383c1)">
              <path d="M 259.203125 166.171875 L 301.496094 166.171875 L 301.496094 325.769531 L 259.203125 325.769531 Z M 259.203125 166.171875 "/>
            </g>
          </g>
          <g clipRule="nonzero" clipPath="url(#a96bd18736)">
            <g clipRule="nonzero" clipPath="url(#53fbf0e616)">
              <path d="M 197.0625 230.40625 L 240.792969 230.40625 L 240.792969 357.507812 L 197.0625 357.507812 Z M 197.0625 230.40625 "/>
            </g>
          </g>
          <path 
            d="M 15.499551 15.502789 L 118.694748 15.50039 " 
            transform="matrix(0.620307,-0.4197,0.4197,0.620307,66.769623,196.712886)"
            strokeWidth="31"
            strokeLinecap="round"
            strokeLinejoin="miter"
            strokeMiterlimit="4"
          />
          <path 
            d="M 15.499477 15.502142 L 78.845162 15.499015 " 
            transform="matrix(0.653453,0.362503,-0.362503,0.653453,146.311708,141.388203)"
            strokeWidth="31"
            strokeLinecap="round"
            strokeLinejoin="miter"
            strokeMiterlimit="4"
          />
          <path 
            d="M 15.498606 15.500352 L 118.698123 15.500875 " 
            transform="matrix(0.620307,-0.4197,0.4197,0.620307,178.310294,177.78822)"
            strokeWidth="31"
            strokeLinecap="round"
            strokeLinejoin="miter"
            strokeMiterlimit="4"
          />
          <g clipRule="nonzero" clipPath="url(#30f4c2ea78)">
            <g clipRule="nonzero" clipPath="url(#093b866760)">
              <g clipRule="nonzero" clipPath="url(#d1419c1cef)">
                <path d="M 273.09375 95.773438 L 301.535156 141.394531 L 261.671875 166.242188 L 233.230469 120.621094 Z M 273.09375 95.773438 "/>
              </g>
            </g>
          </g>
        </g>
      </svg>
    </div>
  );
}; 